import { useMutation } from '@tanstack/react-query';

import {
  deleteV1InviteToJoinById,
  deleteV1UserById,
  postV1InviteToJoin,
  postV1User,
  putV1InviteToJoinById,
  putV1UserById,
  type InviteToJoinPatchBody,
  type InviteToJoinPostBody,
  type UserPostBody,
} from '../../api/generated/pap';
import { invalidateUsers } from './userManagement.queries';

/**
 * Mutation hooks for the Users page: the Invites to Join a Business Partner
 * sends out and its User records. The PUTs hit the same path their own GET
 * reads, so the interceptor attaches `If-Match` from that GET's stored ETag;
 * no manual header handling applies.
 *
 * The invite mutations do not invalidate anything: the Users and Requests pages
 * refetch `useInvitations` themselves through the modals' `onClose(shouldUpdate)`.
 */

export const useCreateInviteToJoin = () =>
  useMutation({
    mutationFn: async (payload: InviteToJoinPostBody) => {
      const { data } = await postV1InviteToJoin({ body: payload, throwOnError: true });
      return data;
    },
  });

export const useUpdateInviteToJoin = () =>
  useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: InviteToJoinPatchBody }) => {
      const { data } = await putV1InviteToJoinById({
        path: { id },
        body: payload,
        throwOnError: true,
      });
      return data;
    },
  });

export const useDeleteInviteToJoin = () =>
  useMutation({
    mutationFn: async (id: string) => {
      await deleteV1InviteToJoinById({ path: { id }, throwOnError: true });
    },
  });

export const useCreateUser = () =>
  useMutation({
    mutationFn: async (payload: UserPostBody) => {
      const { data } = await postV1User({ body: payload, throwOnError: true });
      return data;
    },
    onSuccess: () => {
      invalidateUsers();
    },
  });

// Payload stays `UserPostBody` (as the legacy hook typed it) rather than the PUT's
// `UserPatchBody`: `UserProfile` sends `{ profileIds, identityId }` and the wire body
// must not change. `UserPostBody` is generated and assignable to `UserPatchBody`.
export const useUpdateUser = () =>
  useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: UserPostBody }) => {
      const { data } = await putV1UserById({ path: { id }, body: payload, throwOnError: true });
      return data;
    },
    onSuccess: () => {
      invalidateUsers();
    },
  });

export const useDeleteUser = () =>
  useMutation({
    mutationFn: async (id: string) => {
      await deleteV1UserById({ path: { id }, throwOnError: true });
    },
    onSuccess: () => {
      invalidateUsers();
    },
  });
